<template>
    <div class="composer-root" 
        :class="{ 'focus-filter-exclusion': filterExclusionFocusMode }"
        @dragover.prevent @drop.prevent="onToolboxDrop"
    >
        <canvas-area-zoom-controls
            class="zoom-controls composer-component"
            @zoom-in="$refs.zoom.zoomIn()"
            @zoom-out="$refs.zoom.zoomOut()"
        />

        <horizontal-scroll
            class="horizontal-scroll composer-component"

            :min-left="boundaryPositions.x.min"
            :max-left="boundaryPositions.x.max"
            :position="panX"

            @update:position="panToX($event)"
            @click.native.stop
        />

        <vertical-scroll
            class="vertical-scroll composer-component"

            :min-top="boundaryPositions.y.min"
            :max-top="boundaryPositions.y.max"

            :position="panY"

            @update:position="panToY($event)"
            @click.native.stop
        />
        
        <drag-and-drop-wrapper v-slot:="{jsPlumbInstance}">
            <canvas-area-zoom ref="zoom"
                @scale-changed="zoomChanged(jsPlumbInstance, $event)"
                @pan-changed="panChanged"
                                           
                composer-component-class="composer-component"
                                           
                use-parent-element-as-proxy
            >
                <div class="compose-area" ref="compose-area">
                    <drag-and-drop-draggable
                        v-for="{ 
                            brick, 
                            connectedIncomingConnections, 
                            connectedOutgoingConnections, 
                            brickComponent, 
                            isActive, 
                            isHighlighted
                        } in journeyBricks"

                        :key="brick.id"
                        :js-plumb-instance="jsPlumbInstance"

                        @start="componentMoveStarted($event.el, brick.id)"
                        @stop="brickMoveStopped($event.el, $event.finalPos)"
                    >
                        <brick-square-endpoints
                            :js-plumb-instance="jsPlumbInstance"
                            :brick-id="brick.id"
                            :incoming-connections="brick.incomingConnections"
                            :outgoing-connections="brick.outgoingConnections"
                            
                            @connection-created="$emit('connection-created', $event)"
                        >
                            <component :is="brickComponent"

                                       class="composer-component"
                                       
                                       :is-active="isActive"
                                       :type="brick.type"
                                       :config="brick.config"
                                       :x="brick.x"
                                       :y="brick.y"
                                       :brick="brick"
                                       
                                       :incoming-connections-info="connectedIncomingConnections"
                                       :outgoing-connections-info="connectedOutgoingConnections"

                                       :is-highlighted="isHighlighted"

                                       @add-connection="$emit('add-connection', brick)"
                                       @delete-brick="$emit('delete', { brick: brick, requiresPrompt: $event })"
                                       @settings-changed="$emit('settings-changed', Object.assign({id: brick.id}, $event))"
                            >
                            </component>
                        </brick-square-endpoints>
                    </drag-and-drop-draggable>

                    <brick-connection
                        v-for="{connection, isActive} in journeyBrickConnections"
                        :key="connection.id"
                        
                        :connection="connection"
                        :js-plumb-instance="jsPlumbInstance"
                        :is-active="isActive"
                    >
                        <drag-and-drop-draggable
                            :js-plumb-instance="jsPlumbInstance"

                            @start="componentMoveStarted($event.el, connection.id)"
                            @stop="connectorMoveStopped($event.el, $event.finalPos, connection.sourceBrickId)"
                        >
                            <brick-circle class="composer-component"
                                                  :is-active="isActive"
                                                  :type="connection.destinationBrick.type"
                                                  :config="connection.config"
                                                  :x="connection.connectorX"
                                                  :y="connection.connectorY"
                                                  :connection="connection"

                                                  :is-dragging="draggedComponentId === connection.id"

                                                  @delete-connection-checkpoint="$emit('delete-connection-checkpoint', $event)"
                                                  @change-connection="changeConnection(connection.sourceBrickId, connection.id, $event)"
                                                  @delete-connection="deleteConnection(connection.sourceBrickId, connection.destinationBrickId, $event)"
                            >
                            </brick-circle>
                        </drag-and-drop-draggable>
                    </brick-connection>
                </div>
            </canvas-area-zoom>
        </drag-and-drop-wrapper>
    </div>
</template>

<script>
    import {mapState} from 'vuex';

    import brickDisplayFormats from '@modules/JourneyBuilder/Bricks/brick-display-formats';
    import {brickDefinitions} from '@modules/JourneyBuilder/Bricks/brick-definitions';
    import brickTypes from '@modules/JourneyBuilder/Bricks/brick-types';
    import { checkpointDefinitions } from "@modules/JourneyBuilder/Bricks/Checkpoints";

    import journeyTypes from '@modules/JourneyBuilder/journeyTypes';

    import resourceMixin from "@core/Util/Mixins/resources";
    import journeyBrickEndpointUuidMixin from '@modules/JourneyBuilder/Mixins/journey-brick-endpoint-uuid';
    import injectJourneyMixin from '@modules/JourneyBuilder/Mixins/inject-journey'

    import Modal from "@core/Components/Util/Modal.vue";
    
    import CanvasAreaZoom from "./CanvasAreaZoom.vue";
    import CanvasAreaZoomControls from "./CanvasAreaZoomControls";
    import {HorizontalScroll, VerticalScroll} from "./ScrollBars";
    import DragAndDropWrapper from "./CanvasDragAndDropWrapper";
    import {default as DragAndDropDraggable} from "./CanvasAreaDraggable.vue";
    import DragAndDropEndpoints from "@core/Components/DragAndDrop/DragAndDropEndpoints.vue";
    
    import {
        BrickSquare, 
        BrickCircle,
        BrickOctagon,
        BrickConnection,
        BrickSquareEndpoints
    } from "./Bricks";
    
    import ToolboxDragAction from "@modules/JourneyBuilder/Components/Composer/MenuArea/toolbox-drag-action";

    export default {
        name: 'canvas-area',
        components: {
            Modal,
            
            DragAndDropWrapper,
            DragAndDropDraggable,
            DragAndDropEndpoints,

            CanvasAreaZoom,
            CanvasAreaZoomControls,
            HorizontalScroll,
            VerticalScroll,
            BrickConnection,
            BrickSquareEndpoints,

            BrickCircle
        },
        mixins: [journeyBrickEndpointUuidMixin, resourceMixin, injectJourneyMixin],
        async mounted() {
            //Vue router is not available at the moment due to our page not being loaded as a SPA page
            //When available, extract 'highlightedBrickId' querystring parameter
            const highlightedBrickIdParameter = document.location.search
                .substring(1) //Skip '?'
                .split('&')
                .find(query => query.startsWith('highlightedBrickId='));
            
            if(!highlightedBrickIdParameter) {
                //Execute our scaling at the end of the current event queue (= after everything has rendered)
                setTimeout(() => this.fitCanvasToJourney(), 0);
                return;
            }
            
            //highlightedBrickId=someGuid
            const [, highlightedBrickId] = highlightedBrickIdParameter.split('=');

            this.highlightedBrickId = highlightedBrickId;

            //Execute our pan at the end of the current event queue (= after everything has rendered)
            setTimeout(
                () => this.panToBrickCenter(highlightedBrickId), 
                0
            );
        },
        methods: {
            panToBrickCenter(brickId){
                const brick = this.journey.bricks.find(b => b.id === brickId);
                if(!brick){
                    return;
                }
                
                const {
                    width: brickWidth,
                    height: brickHeight
                } = this.getBrickElementBoxSize();

                const {
                    width: containerWidth,
                    height: containerHeight
                } = this.$el.getBoundingClientRect();

                this.panToX(brick.x - (containerWidth / 2) + (brickWidth / 2));
                this.panToY(brick.y - (containerHeight / 2) + (brickHeight / 2));
            },

            /**
             * Zoom and pan to display the complete journey
             */
            fitCanvasToJourney() {
                //Empty journey - back out early
                //Executing without any bricks breaks our zoom calculation, setting our zoom to `NaN`
                if(this.journey.bricks.length === 0) {
                    return;
                }
                
                const { height: containerHeight, width: containerWidth } =  this.$refs['compose-area'].getBoundingClientRect();
                
                const {
                    x: {
                        min: minBrickX,
                        max: maxBrickX
                    },
                    y: {
                        min: minBrickY,
                        max: maxBrickY
                    }
                } = this.boundaryPositions;
                
                //Step 1: Check if any bricks are (partially) rendered out of bounds
                if(
                    Math.abs(minBrickY - maxBrickY) > containerHeight ||
                    Math.abs(minBrickX - maxBrickX) > containerWidth
                ) {
                    //Step 2: Calculate required zoom level to properly fit bricks
                    const newScale = Math.min(
                        containerHeight / Math.abs(minBrickY - maxBrickY),
                        containerWidth / Math.abs(minBrickX - maxBrickX)
                    );

                    //Step 3: Apply our new scale
                    this.$refs.zoom.setScale(newScale);
                }
                
                //Step 4: Calculate the offsets to display all bricks with an even amount of offset in between bounds
                this.panToY(
                    -((containerHeight - maxBrickY - minBrickY) / 2)
                );

                this.panToX(
                    -((containerWidth - maxBrickX - minBrickX) / 2)
                );
            },

            /** 
             * @param {HTMLElement} el 
             * @param {String} componentId
             * */
            componentMoveStarted(el, componentId){
                //We want to add the dragging class manually
                //due to Vue's repaint causing a noticeable slowdown
                el.classList.add('dragging')
                this.draggedComponentId = componentId;
            },

            /**
             * @param {HTMLElement} el
             * @param {Number[]} finalPos
             * */
            brickMoveStopped(el, finalPos) {
                const brickId = this.draggedComponentId;
                this.draggedComponentId = null;
                
                el.classList.remove('dragging');
                const [x, y] = finalPos;
                
                this.$emit('brick-moved', {
                    brickId: brickId, 
                    x: x, 
                    y: y
                });
            },

            /**
             * @param {HTMLElement} el
             * @param {Number[]} finalPos
             * @param {String} sourceBrickId
             */
            connectorMoveStopped(el, finalPos, sourceBrickId){
                const connectionId = this.draggedComponentId;
                this.draggedComponentId = null;

                el.classList.remove('dragging');
                const [x, y] = finalPos;

                this.$emit('connector-moved', {
                    connectionId: connectionId,
                    sourceBrickId: sourceBrickId,
                    connectorX: x,
                    connectorY: y
                });
            },
            
            zoomChanged(jsPlumbInstance, detail){
                jsPlumbInstance.setZoom(detail.scale);
                
                this.panChanged(detail);
            },
            panChanged({x, y, scale}) {
                //HACK: prevent new bricks from being added outside of visible area
                const extraXY = scale > 1 ? 400 : 0;
                
                this.scale = scale;
                this.panX = -x;
                this.panY = -y;
                
                //X/Y are reversed by our panning framework
                this.$emit('top-left-changed', {
                    x: (-x) + extraXY,
                    y: (-y) + extraXY
                });
            },
            panToX(newX){
                //Invert X for panzoom
                this.$refs.zoom.panToX(-newX);
            },
            panToY(newY){
                //Invert Y for panzoom
                this.$refs.zoom.panToY(-newY);
            },
            changeConnection(sourceBrickId, connectionId, event){
                this.$emit('change-connection', {
                    sourceBrickId: sourceBrickId,
                    connectionId: connectionId,
                    model: event
                });
            },
            deleteConnection(sourceBrickId, destinationBrickId, event){
                this.$emit('delete-connection', {
                    sourceBrickId: sourceBrickId,
                    destinationBrickId: destinationBrickId,
                    connectionId: event
                });
            },
            deleteCheckpoint(sourceBrickId, connectionId, type) {
                this.$emit('delete-connection-checkpoint', { sourceBrickId, connectionId, type });
            },
            onToolboxDrop(event){
                const composeArea = this.$refs['compose-area'];
                
                //For the time being we can assume the only drop events will be from our toolbox
                const action = ToolboxDragAction.deserializeFromEvent(event);

                const { top, left } = composeArea.getBoundingClientRect();
                const { clientX, clientY } = event;
                
                //Calculate drop position based on X - absolute left of our container divided by our scaler
                const location = {
                    composerX: (clientX - left) / this.scale,
                    composerY: (clientY - top) / this.scale,
                    absoluteX: clientX,
                    absoluteY: clientY
                };
                
                switch (action.type){
                    case ToolboxDragAction.TYPE_BRICK:
                        this.handleToolboxBrickDragAction(action.data, location);
                        break;
                    case ToolboxDragAction.TYPE_CHECKPOINT:
                        this.handleToolboxCheckpointDragAction(action.data, location);
                        break;
                }
            },
            handleToolboxBrickDragAction(data, location){
                this.$emit('add-brick-at-location', {
                    brickDefinition: data,
                    location: {
                        x: location.composerX,
                        y: location.composerY
                    }
                })
            },
            handleToolboxCheckpointDragAction(data, location){
                const { name: checkpointName, elementWidth, offsetX, offsetY } = data;
                const xStart = location.absoluteX + offsetX;
                const yStart = location.absoluteY + offsetY;
                const xEnd = xStart + elementWidth;
                
                //Search for a connection element diagonally, in a cross
                //our source element is a square, thus looking at either x or y should suffice
                const connectionInfoElement = Array.from({length: (xEnd - xStart) / 2})
                    .flatMap((_, index) => {
                        const step = index * 2;
                        
                        return [
                            document.elementFromPoint(xStart + step, yStart + step),
                            document.elementFromPoint(xEnd - step, yStart + step),
                        ];
                    })
                    .find(el => el && el.closest('[data-jc-connection-id]'));
                
                if(!connectionInfoElement){
                    return;
                }
                
                //Find out to which connection our path belongs
                const connectionId = connectionInfoElement.closest('[data-jc-connection-id]').dataset['jcConnectionId'];
                const {connection} = this.journeyBrickConnections.find(bC => bC.connection.id === connectionId);
                
                const checkpoint = checkpointDefinitions[checkpointName];
                
                //Prevent activating a condition twice
                if(checkpoint.isActiveForConnection(connection)) {
                    return;
                }

                checkpoint.activateForConnection(this.journey, connection);
            },
            
            /** @return {{ width: Number, height: Number }} */
            getBrickElementBoxSize(){
                //brick width/height are determined via CSS `rem`, thus only known at runtime
                return {
                    //For now; assume bricks are 125/125 by default
                    width: 125,
                    height: 125
                }
            }
        },
        computed: {
            ...mapState('journeyBuilder/composer', ['filterExclusionFocusMode']),
            journeyBricks(){
                //Umbrella journeys only allow 'journey' bricks
                //these bricks will always be active for umbrella journeys
                //all other bricks will be marked as inactive
                const isActiveFn = this.isUmbrellaJourney ?
                    brick => brick.type === brickTypes.journey :
                    brick => brick.isActive();
                
                const isHighlightedFn = this.highlightedBrickId ?
                    brick => brick.id === this.highlightedBrickId :
                    _ => false;
                
                const getBrickComponentFn = brick => brickDefinitions[brick.type].displayFormat === brickDisplayFormats.octagon ?
                    BrickOctagon :
                    BrickSquare;
                
                return this.journey.bricks.map(brick => ({
                    brick,
                    
                    brickComponent: getBrickComponentFn(brick),
                    isActive: isActiveFn(brick),
                    isHighlighted: isHighlightedFn(brick),

                    connectedIncomingConnections: brick.incomingConnections.filter(c => c.isConnected),
                    connectedOutgoingConnections: brick.outgoingConnections.filter(c => c.isConnected)
                }));
            },
            journeyBrickConnections(){
                //Umbrella journey connections are never active
                const isActiveFn = this.isUmbrellaJourney ?
                    _ => false :
                    connection => connection.isActive();
                
                return this.journey.bricks.reduce((connections, brick) => {
                    brick.outgoingConnections.forEach(connection => {
                        if(connection.isConnected){
                            connections.push({
                                connection,
                                isActive: isActiveFn(connection)
                            });
                        }
                    });
                    
                    return connections;
                }, []);
            },
            boundaryPositions(){
                const brickElementBoxSize = this.getBrickElementBoxSize();
                
                const BRICK_WIDTH = brickElementBoxSize.width;
                const BRICK_HEIGHT = brickElementBoxSize.height;
                const SCROLLBAR_SIZE = 15;
                
                //Assume checkpoints always get put in between bricks for the time being
                const minBrickX = this.journey.bricks.reduce((minX, brick) => Math.min(brick.x, minX), Number.MAX_VALUE);
                const maxBrickX = this.journey.bricks.reduce((maxX, brick) => Math.max(brick.x, maxX), Number.MIN_VALUE) + BRICK_WIDTH + SCROLLBAR_SIZE;

                const minBrickY = this.journey.bricks.reduce((minY, brick) => Math.min(brick.y, minY), Number.MAX_VALUE);
                const maxBrickY = this.journey.bricks.reduce((maxY, brick) => Math.max(brick.y, maxY), Number.MIN_VALUE) + BRICK_HEIGHT + SCROLLBAR_SIZE;
                
                return {
                    x: {
                        min: minBrickX,
                        max: maxBrickX
                    },
                    y: {
                        min: minBrickY,
                        max: maxBrickY 
                    }
                };
            },
            isUmbrellaJourney(){
                return this.journeyType === journeyTypes.umbrella;
            }
        },
        data: () => ({
            draggedComponentId: null,
            scale: 1,
            
            highlightedBrickId: undefined,
            panX: 0,
            panY: 0
        }),
        props: {
            journeyType:0
        }
    }
</script>

<style scoped lang="scss">
    @import "~@modules/JourneyBuilder/Style/journey-builder-composer.scss";
</style>